import React, { useState, useEffect } from 'react';
import { Button } from './ui/button';
import { ArrowRight, Shield, Zap, FileCheck, DollarSign, FileText, CheckCircle2, AlertTriangle, Sparkles } from 'lucide-react';

// Animated Document Conversion Visual Component
const DocumentConversionAnimation = () => {
  const [phase, setPhase] = useState(0); // 0: messy, 1: converting, 2: clean
  
  useEffect(() => {
    const interval = setInterval(() => {
      setPhase(prev => (prev + 1) % 3);
    }, 3000);
    return () => clearInterval(interval);
  }, []);

  return (
    <div className="relative w-full max-w-md mx-auto mb-12">
      {/* Floating particles during conversion */}
      {phase === 1 && (
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          {[...Array(8)].map((_, i) => (
            <div
              key={i}
              className="absolute w-2 h-2 bg-blue-400 rounded-full animate-ping"
              style={{
                left: `${20 + Math.random() * 60}%`,
                top: `${20 + Math.random() * 60}%`,
                animationDelay: `${i * 0.15}s`,
                animationDuration: '1s'
              }}
            />
          ))}
        </div>
      )}
      
      <div className="flex items-center justify-center gap-4 md:gap-8">
        {/* Before Document (Messy) */}
        <div 
          className={`relative transition-all duration-700 ease-in-out ${
            phase === 0 ? 'scale-100 opacity-100' : phase === 1 ? 'scale-95 opacity-60' : 'scale-90 opacity-40'
          }`}
        >
          <div className="w-28 h-36 md:w-32 md:h-40 bg-gradient-to-br from-slate-100 to-slate-200 rounded-lg shadow-lg border border-slate-300 p-3 relative overflow-hidden">
            {/* Messy document lines */}
            <div className="space-y-2">
              <div className="h-2 bg-slate-400/60 rounded w-full"></div>
              <div className="h-2 bg-slate-400/40 rounded w-3/4"></div>
              <div className="h-2 bg-red-400/50 rounded w-full"></div>
              <div className="h-2 bg-slate-400/30 rounded w-5/6"></div>
              <div className="h-2 bg-yellow-400/50 rounded w-2/3"></div>
              <div className="h-2 bg-slate-400/40 rounded w-full"></div>
              <div className="h-2 bg-slate-400/20 rounded w-4/5"></div>
            </div>
            {/* Warning badge */}
            <div className="absolute -top-2 -right-2 bg-amber-500 rounded-full p-1.5 shadow-md">
              <AlertTriangle className="w-3 h-3 text-white" />
            </div>
            {/* Coffee stain effect */}
            <div className="absolute bottom-2 right-2 w-6 h-6 bg-amber-200/40 rounded-full blur-sm"></div>
          </div>
          <p className="text-xs text-slate-500 mt-2 text-center font-medium">Complex Contract</p>
        </div>

        {/* Conversion Arrow */}
        <div className={`flex flex-col items-center transition-all duration-500 ${phase === 1 ? 'scale-110' : 'scale-100'}`}>
          <div className={`relative p-3 rounded-full ${
            phase === 1 
              ? 'bg-gradient-to-r from-blue-500 to-indigo-500 shadow-lg shadow-blue-300' 
              : 'bg-gradient-to-r from-blue-100 to-indigo-100'
          }`}>
            <Sparkles className={`w-5 h-5 ${phase === 1 ? 'text-white animate-spin' : 'text-blue-500'}`} style={{ animationDuration: '2s' }} />
          </div>
          <div className={`mt-2 flex items-center gap-1 transition-all duration-300 ${phase === 1 ? 'opacity-100' : 'opacity-0'}`}>
            <span className="text-xs font-semibold text-blue-600">Converting</span>
            <span className="flex gap-0.5">
              <span className="w-1 h-1 bg-blue-500 rounded-full animate-bounce" style={{ animationDelay: '0ms' }}></span>
              <span className="w-1 h-1 bg-blue-500 rounded-full animate-bounce" style={{ animationDelay: '150ms' }}></span>
              <span className="w-1 h-1 bg-blue-500 rounded-full animate-bounce" style={{ animationDelay: '300ms' }}></span>
            </span>
          </div>
        </div>

        {/* After Document (Clean & Analyzed) */}
        <div 
          className={`relative transition-all duration-700 ease-in-out ${
            phase === 2 ? 'scale-100 opacity-100' : phase === 1 ? 'scale-95 opacity-60' : 'scale-90 opacity-40'
          }`}
        >
          <div className="w-28 h-36 md:w-32 md:h-40 bg-gradient-to-br from-white to-blue-50 rounded-lg shadow-lg border border-blue-200 p-3 relative overflow-hidden">
            {/* Clean document lines with highlights */}
            <div className="space-y-2">
              <div className="h-2 bg-slate-700/80 rounded w-full"></div>
              <div className="h-2 bg-slate-600/60 rounded w-3/4"></div>
              <div className="h-2 bg-green-400/70 rounded w-full border border-green-300"></div>
              <div className="h-2 bg-slate-500/50 rounded w-5/6"></div>
              <div className="h-2 bg-blue-400/60 rounded w-2/3 border border-blue-300"></div>
              <div className="h-2 bg-slate-500/40 rounded w-full"></div>
              <div className="h-2 bg-slate-400/30 rounded w-4/5"></div>
            </div>
            {/* Success badge */}
            <div className="absolute -top-2 -right-2 bg-green-500 rounded-full p-1.5 shadow-md">
              <CheckCircle2 className="w-3 h-3 text-white" />
            </div>
            {/* Analysis indicator */}
            <div className="absolute bottom-1 left-1 right-1 bg-gradient-to-r from-green-100 to-blue-100 rounded px-1.5 py-0.5 flex items-center gap-1">
              <div className="w-1.5 h-1.5 bg-green-500 rounded-full"></div>
              <span className="text-[8px] font-semibold text-green-700">Analyzed</span>
            </div>
          </div>
          <p className="text-xs text-slate-500 mt-2 text-center font-medium">Clear & Understood</p>
        </div>
      </div>

      {/* Progress indicator */}
      <div className="flex justify-center gap-2 mt-4">
        {[0, 1, 2].map(i => (
          <div
            key={i}
            className={`h-1.5 rounded-full transition-all duration-300 ${
              i === phase ? 'w-6 bg-blue-500' : 'w-1.5 bg-slate-300'
            }`}
          />
        ))}
      </div>
    </div>
  );
};

const Hero = () => {
  return (
    <section className="relative bg-gradient-to-br from-indigo-50 via-white to-blue-50 dark:from-slate-900 dark:via-slate-950 dark:to-slate-900 py-20 lg:py-32 overflow-hidden transition-colors duration-300">
      {/* Background decorative elements */}
      <div className="absolute inset-0">
        <div className="absolute top-20 left-10 w-32 h-32 bg-gradient-to-br from-blue-400/20 to-indigo-400/20 dark:from-blue-500/10 dark:to-indigo-500/10 rounded-full blur-3xl"></div>
        <div className="absolute bottom-20 right-10 w-40 h-40 bg-gradient-to-br from-indigo-400/20 to-slate-400/20 dark:from-indigo-500/10 dark:to-slate-500/10 rounded-full blur-3xl"></div>
        <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-60 h-60 bg-gradient-to-br from-blue-300/10 to-indigo-300/10 dark:from-blue-500/5 dark:to-indigo-500/5 rounded-full blur-3xl"></div>
      </div>

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center max-w-4xl mx-auto">
          {/* Hero Badge */}
          <div className="inline-flex items-center px-6 py-3 bg-gradient-to-r from-green-100 to-emerald-100 dark:from-green-900/30 dark:to-emerald-900/30 border border-green-200 dark:border-green-700 rounded-full text-sm font-medium text-green-800 dark:text-green-300 mb-8 shadow-sm">
            <DollarSign className="h-4 w-4 mr-2 text-green-600 dark:text-green-400" />
            Save Thousands on Legal Document Costs
          </div>

          {/* Main Headline */}
          <h1 className="text-5xl lg:text-6xl font-bold text-slate-900 dark:text-white mb-6 leading-tight">
            Convert & Understand
            <span className="block text-slate-800 dark:text-slate-200">Your Legal Documents</span>
            <span className="block text-transparent bg-clip-text bg-gradient-to-r from-blue-600 via-indigo-600 to-slate-700 dark:from-blue-400 dark:via-indigo-400 dark:to-slate-400">
              Without a Lawyer
            </span>
          </h1>

          {/* Subheadline */}
          <p className="text-xl text-slate-600 dark:text-slate-400 mb-8 max-w-3xl mx-auto leading-relaxed">
            Convert contracts between 30+ formats, get plain-English explanations of complex terms, 
            and scan paper documents — all without expensive attorney fees.
          </p>

          {/* Document Conversion Animation */}
          <DocumentConversionAnimation />

          {/* CTA Buttons */}
          <div className="flex flex-col sm:flex-row gap-4 justify-center items-center mb-16">
            <Button 
              size="lg" 
              data-testid="hero-start-free-btn"
              className="bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white px-8 py-4 text-lg font-semibold shadow-lg hover:shadow-xl transition-all duration-300 transform hover:-translate-y-1 border-0"
              onClick={() => {
                document.getElementById('processor').scrollIntoView({behavior: 'smooth'});
              }}
            >
              Start For Free
              <ArrowRight className="ml-2 h-5 w-5" />
            </Button>
            <Button 
              variant="outline" 
              size="lg"
              data-testid="hero-how-it-works-btn"
              className="border-2 border-slate-300 text-slate-700 hover:bg-gradient-to-r hover:from-slate-50 hover:to-blue-50 hover:border-blue-300 hover:text-blue-700 px-8 py-4 text-lg font-semibold transition-all duration-300"
              onClick={() => {
                document.getElementById('features').scrollIntoView({behavior: 'smooth'});
              }}
            >
              See How It Works
            </Button>
          </div>

          {/* Feature Highlights */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mt-20">
            <div className="group flex flex-col items-center p-8 bg-gradient-to-br from-white to-blue-50/50 rounded-2xl shadow-sm border border-blue-100/50 hover:shadow-lg hover:shadow-blue-100/50 transition-all duration-300 transform hover:-translate-y-2">
              <div className="p-4 bg-gradient-to-br from-blue-500 to-indigo-500 rounded-xl mb-6 group-hover:scale-110 transition-transform duration-300">
                <FileCheck className="h-8 w-8 text-white" />
              </div>
              <h3 className="text-xl font-semibold text-slate-900 mb-3">Easy Document Conversion</h3>
              <p className="text-slate-600 text-center leading-relaxed">Convert contracts, invoices, and business docs between PDF, Word, and 30+ formats.</p>
            </div>

            <div className="group flex flex-col items-center p-8 bg-gradient-to-br from-white to-indigo-50/50 rounded-2xl shadow-sm border border-indigo-100/50 hover:shadow-lg hover:shadow-indigo-100/50 transition-all duration-300 transform hover:-translate-y-2">
              <div className="p-4 bg-gradient-to-br from-indigo-500 to-slate-600 rounded-xl mb-6 group-hover:scale-110 transition-transform duration-300">
                <Zap className="h-8 w-8 text-white" />
              </div>
              <h3 className="text-xl font-semibold text-slate-900 mb-3">AI-Powered Insights</h3>
              <p className="text-slate-600 text-center leading-relaxed">Understand your contracts better with plain-English explanations and risk alerts.</p>
            </div>

            <div className="group flex flex-col items-center p-8 bg-gradient-to-br from-white to-green-50 rounded-2xl shadow-sm border border-green-100 hover:shadow-lg hover:shadow-green-100/50 transition-all duration-300 transform hover:-translate-y-2">
              <div className="p-4 bg-gradient-to-br from-green-500 to-emerald-600 rounded-xl mb-6 group-hover:scale-110 transition-transform duration-300">
                <DollarSign className="h-8 w-8 text-white" />
              </div>
              <h3 className="text-xl font-semibold text-slate-900 mb-3">Save 90% on Legal Costs</h3>
              <p className="text-slate-600 text-center leading-relaxed">Handle routine legal documents yourself instead of paying $300+/hour for attorneys.</p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Hero;